import React, { createContext, useContext, useEffect, useState } from "react";
import { getReferalPrograms } from "@/api/base";
import { useUserInfoStore } from "@/store/userInfoStore";
import { ConditionI, ServiceType } from "@/types/referals";
import { IDS } from "@/components/InviteReferralsSection/idsForEats";

interface ReferalProgramsContextValue {
  referalProgramsLoading: boolean;
  referalPrograms: ConditionI[];
}

const ReferalProgramsContext = createContext<
  ReferalProgramsContextValue | undefined
>(undefined);

export const ReferalProgramsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [referalProgramsLoading, setReferalProgramsLoading] = useState<boolean>(
    true
  );
  const [referalPrograms, setReferalPrograms] = useState<ConditionI[]>([]);
  const serviceType = useUserInfoStore((s) => s.serviceType);
  const yandexId = useUserInfoStore((s) => s.yandexId);

  const loadData = async (serType: ServiceType[]) => {
    try {
      setReferalProgramsLoading(true);

      const res = await getReferalPrograms(serType, yandexId);
      setReferalPrograms(res);
    } catch (e) {
      console.log(e);
    } finally {
      setReferalProgramsLoading(false);
    }
  };

  useEffect(() => {
    if (!serviceType || !yandexId) {
      setReferalPrograms([]);
      setReferalProgramsLoading(false);
      return;
    }

    const serType: ServiceType[] = (() => {
      if (IDS.has(yandexId) && serviceType === "delivery")
        return ["delivery", "taxi", "eats"];

      if (serviceType === "taxi" || serviceType === "delivery")
        return ["delivery", "taxi"];

      if (serviceType === "eats") return ["eats"];

      return [];
    })();

    loadData(serType);
  }, [serviceType, yandexId]);

  return (
    <ReferalProgramsContext.Provider
      value={{ referalProgramsLoading, referalPrograms }}
    >
      {children}
    </ReferalProgramsContext.Provider>
  );
};

export const useReferalProgramsDataContext = (): ReferalProgramsContextValue => {
  const ctx = useContext(ReferalProgramsContext);
  if (!ctx) {
    throw new Error(
      "useReferalProgramsDataContext must be used within ReferalProgramsProvider"
    );
  }
  return ctx;
};
